/**
 * Phase 3 marketing copy for the in-app landing page only.
 * SEO / canonical strings: keep aligned with Helmet usage on this page (and backend only if you add SSR meta later).
 */
export const LANDING_CANONICAL_ORIGIN = 'https://tranzit.io' as const;

export const LANDING_SEO = {
  title: 'Tranzit.io — The Operating System for Freight',
  metaTitle: 'Tranzit.io — The Operating System for Freight | Carrier Verification & Logistics Platform',
  description:
    'One ecosystem for carriers, brokers, and shippers. FMCSA-aligned verification, compliance tooling, and freight workflow software. Tranzit is not a motor carrier, freight broker, or freight forwarder.',
  ogTitle: 'Tranzit.io — The Operating System for Freight',
  /** Alt text for OG/Twitter preview image (Helmet). */
  ogImageAlt: 'Tranzit.io — operating system for freight logistics and verification',
} as const;

export const LANDING_HERO = {
  title: 'The Operating System for Freight',
  subtitle: 'One ecosystem for carriers, brokers, and shippers.',
} as const;

export const LANDING_TRUST = {
  tagline: 'Built for the modern freight ecosystem.',
} as const;

export const LANDING_PROBLEM = {
  title: 'The Freight Problem',
  intro:
    'Teams juggle disconnected tools while fraud and verification chaos create risk on every load. The status quo costs time, trust, and margin.',
  cards: [
    {
      title: 'Load Board/Partnerships',
      text: 'Fragmented postings and blind handoffs make it hard to know who you are really working with.',
    },
    {
      title: 'Compliance',
      text: 'Siloed checks and stale data leave gaps between onboarding, ops, and audit-ready records.',
    },
    {
      title: 'Traceability',
      text: 'During the live haul—the stretch where partnerships are truly tested—carrier and freight status splinters across calls, texts, and ad hoc updates, so no one shares one authoritative view of what is moving and who is accountable.',
    },
    {
      title: 'Transparency',
      text: 'Paperwork, transport milestones, pricing, and payments travel through different threads and systems; versions conflict, timing drifts, and parties reconcile disputes instead of operating from a single, trusted record.',
    },
  ] as const,
} as const;

export const LANDING_SOLUTION = {
  title: 'Single Ecosystem to Replace Layered, Disconnected Apps',
  hubItems: [
    'Carrier Compliance and Verification (VVID)',
    'Broker and Shipper Tools',
    'Load Marketplace',
    'Tracking and Transparency',
    'Partnership Creation',
    'Factoring and Payment Solutions',
  ] as const,
} as const;

export const LANDING_PRODUCT_MODULES = {
  sectionTitle: 'Product modules',
  modules: [
    {
      title: 'Carrier Compliance (VVID)',
      text: 'FMCSA-aligned verification and the proprietary VVID layer so you know who you are working with—load after load.',
      icon: 'shield' as const,
    },
    {
      title: 'Broker and Shipper Tools',
      text: 'Workflows built for how brokers and shippers run freight—less tool-switching, clearer handoffs.',
      icon: 'id' as const,
    },
    {
      title: 'Load Marketplace',
      text: 'Discover and connect in a transparent workspace without losing operational context.',
      icon: 'store' as const,
    },
  ] as const,
  slideshowHeading: 'See it in action',
} as const;

export const LANDING_NETWORK = {
  title: 'Network effect',
  body:
    'The more companies that join Tranzit, the stronger verification, repeat partnerships, and shared context become for everyone on the network.',
  nodes: ['Carriers', 'Brokers', 'Shippers', 'Inspectors'] as const,
} as const;

/** Decorative diagram copy (screen readers: SVGs also include <title> / descriptions). */
export const LANDING_DIAGRAM_ARIA = {
  problem:
    'Diagram: four disconnected problem areas—load board and partnerships, compliance, traceability, and transparency—shown as separate nodes with tangled, non-linear links between them.',
  solution:
    'Diagram: Tranzit as a central hub connected to carrier compliance and VVID, broker and shipper tools, load marketplace, tracking and transparency, partnership creation, and factoring and payment solutions.',
  network:
    'Diagram: Tranzit at the center with carriers, brokers, shippers, and inspectors connected around it.',
} as const;

export const LANDING_SECURITY = {
  title: 'Secure. Compliant. Ready to scale.',
  intro:
    'FMCSA integrated. SOC 2 compliant. Fraud prevention and carrier identity verification designed for high-stakes freight operations.',
  cards: [
    {
      pillar: 'FMCSA integrated',
      headline: 'Authoritative carrier context',
      text: 'Bring FMCSA-aligned data into workflows so decisions are grounded in up-to-date regulatory context. Users remain responsible for compliance and business decisions.',
    },
    {
      pillar: 'SOC 2 compliant',
      headline: 'Controls built for scale',
      text: 'Security and operational practices designed to meet enterprise expectations as you grow usage across teams and partners.',
    },
    {
      pillar: 'Fraud prevention',
      headline: 'VVID and traceability',
      text: 'Combat double brokering and fraud with the proprietary VVID system and AI-assisted signals for verification and traceability. Tranzit does not process, escrow, or handle freight payments.',
    },
    {
      pillar: 'Carrier identity',
      headline: 'Know who you are working with',
      text: 'Stronger identity and document verification so brokers, shippers, and carriers can partner with confidence—without resetting trust every load.',
    },
  ] as const,
} as const;

export const LANDING_FINAL_CTA = {
  line: 'Join the Freight Network Being Built for the Next Decade',
  primaryLabel: 'Get Started',
  secondaryLabel: 'Book Demo',
} as const;
